import {
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import {
  Visibility,
  TrendingUp,
  CalendarToday,
  Favorite,
} from '@mui/icons-material';
import { CommonStyles } from '../styles/CommonStyles';
import { useQuery } from '@apollo/client';
import { GET_ALL_COMPLETED_QUIZZES_FOR_USER } from '../graphql/getAllCompletedQuizzesForUser';
import { format } from 'date-fns';
import {
  GetAllCompletedQuizzesForUserQuery,
  Quiz,
} from '../__generated__/graphql';
import { useNavigate } from 'react-router-dom';
import { tagToColor } from '../types/Tag';

const CompletedRounds = () => {
  const { data, loading } = useQuery<GetAllCompletedQuizzesForUserQuery>(
    GET_ALL_COMPLETED_QUIZZES_FOR_USER,
  );
  const navigate = useNavigate();

  if (loading) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }

  const handleRowClick = (quizId: string) => {
    navigate(`/quiz/${quizId}`);
  };

  const completedQuizzes = data?.getAllCompletedQuizzesForUser || [];
  const totalRounds = completedQuizzes.length;
  const uniqueOrgans = new Set(completedQuizzes.map((quiz) => quiz.tag)).size;
  const recentRounds = completedQuizzes.filter((quiz) => {
    const quizDate = new Date(Number(quiz.updatedAt));
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return quizDate > weekAgo;
  }).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Enhanced Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.2rem' },
            color: 'rgb(57, 73, 142)',
            mb: 1,
            textAlign: 'center',
          }}
        >
          My Completed Rounds
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgb(100, 100, 100)',
            textAlign: 'center',
            fontSize: '1.1rem',
          }}
        >
          Track your quiz progress and review your performance
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              color: 'rgb(57, 73, 142)',
              borderRadius: 3,
              boxShadow:
                '0 4px 12px rgba(57, 73, 142, 0.3), 0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <TrendingUp sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {totalRounds}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, color: 'black' }}>
                Total Rounds Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              color: 'rgb(57, 73, 142)',
              borderRadius: 3,
              boxShadow:
                '0 4px 12px rgba(57, 73, 142, 0.3), 0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <CalendarToday sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {recentRounds}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, color: 'black' }}>
                Rounds This Week
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              color: 'rgb(57, 73, 142)',
              borderRadius: 3,
              boxShadow:
                '0 4px 12px rgba(57, 73, 142, 0.3), 0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            <CardContent sx={{ textAlign: 'center', p: 3 }}>
              <Favorite sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {uniqueOrgans}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, color: 'black' }}>
                Organs Studied
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Enhanced Table */}
      {totalRounds > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: 'rgb(57, 73, 142)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 2,
                    px: 3,
                  }}
                >
                  Date Completed
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'rgb(57, 73, 142)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 2,
                    px: 3,
                  }}
                >
                  Organ
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'rgb(57, 73, 142)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1rem',
                    py: 2,
                    px: 3,
                    textAlign: 'center',
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {completedQuizzes.map((quiz) => (
                <TableRow
                  key={quiz.id}
                  onClick={() => handleRowClick(quiz.id)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: 'rgba(57, 73, 142, 0.05)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'rgba(0,0,0,0.02)',
                    },
                  }}
                >
                  <TableCell sx={{ py: 2, px: 3, fontSize: '0.95rem' }}>
                    {quiz.updatedAt
                      ? format(Number(quiz.updatedAt), 'MMM d, yyyy h:mm a')
                      : 'N/A'}
                  </TableCell>
                  <TableCell sx={{ py: 2, px: 3 }}>
                    <Chip
                      label={quiz.tag}
                      sx={{
                        backgroundColor: tagToColor[quiz.tag] || '#BDBDBD',
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        height: 32,
                        '& .MuiChip-label': {
                          px: 2,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 2, px: 3, textAlign: 'center' }}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(quiz.id);
                      }}
                      sx={{
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'rgba(57, 73, 142, 0.1)',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Empty State */
        <Card
          sx={{
            textAlign: 'center',
            p: 6,
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: 'rgb(245, 247, 250)',
          }}
        >
          <CardContent>
            <TrendingUp
              sx={{
                fontSize: 80,
                color: 'white',
                mb: 2,
                opacity: 0.7,
              }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, mb: 2, color: 'rgb(57, 73, 142)' }}
            >
              No Completed Rounds Yet
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: 'rgb(100, 100, 100)', mb: 3 }}
            >
              Start taking quizzes to see your completed rounds here. Your
              progress will be tracked and displayed in this table.
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgb(150, 150, 150)' }}>
              Complete your first quiz to get started!
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CompletedRounds;
